import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Plus, X, Calendar, User, History, MessageSquare, Check } from 'lucide-react';
import { toast } from 'sonner';
import { normalizeNote } from '../utils/normalizeNote';
import UserAvatar from '../components/UserAvatar';
import VoiceInput from '../components/VoiceInput';

const Notation = ({ user }) => {
  const [searchParams] = useSearchParams();
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [categorie, setCategorie] = useState('Briefing');
  const [item, setItem] = useState('');
  const [customItem, setCustomItem] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [note, setNote] = useState(5);
  const [commentaire, setCommentaire] = useState('');
  const [commentairePrive, setCommentairePrive] = useState('');
  // Date de la note (par défaut aujourd'hui)
  const [dateNote, setDateNote] = useState(new Date().toISOString().split('T')[0]);
  
  // Items ajoutés par le formateur pour les épreuves formatives
  const [customEpreuves, setCustomEpreuves] = useState([]);
  // Dernière note pour le memo
  const [stagiaireNotes, setStagiaireNotes] = useState([]);
  const [lastNote, setLastNote] = useState(null);
  // Confirmation visuelle
  const [justSaved, setJustSaved] = useState(false);
  const [savedInfo, setSavedInfo] = useState(null);

  useEffect(() => {
    loadStagiaires();
    loadCustomEpreuves();
  }, []);

  useEffect(() => {
    if (selectedStagiaire) loadStagiaireNotes();
  }, [selectedStagiaire]);

  useEffect(() => {
    if (!selectedStagiaire || !categorie || !item) {
      setLastNote(null);
      return;
    }
    const matching = stagiaireNotes
      .filter(n => n.categorie === categorie && n.item === item)
      .sort((a, b) => new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0));
    setLastNote(matching.length > 0 ? matching[0] : null);
  }, [selectedStagiaire, categorie, item, stagiaireNotes]);

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      // Use URL param if provided, otherwise default to first
      const urlStagiaire = searchParams.get('stagiaire');
      if (urlStagiaire && response.data.some(s => s.user_id === urlStagiaire)) {
        setSelectedStagiaire(urlStagiaire);
      } else if (response.data.length > 0) {
        setSelectedStagiaire(response.data[0].user_id);
      }
    } catch (error) {
    }
  };

  const loadCustomEpreuves = async () => {
    try {
      const response = await axios.get('/epreuves-formatives');
      setCustomEpreuves(response.data.map(e => e.sujet));
    } catch (error) {
    }
  };

  const loadStagiaireNotes = async () => {
    try {
      const response = await axios.get(`/notes/stagiaire/${selectedStagiaire}`);
      setStagiaireNotes(response.data || []);
    } catch (error) {
      setStagiaireNotes([]);
    }
  };

  const categories = {
    'Briefing': ['BRIEFING VNM', 'BRIEFING Engins tractés', 'BRIEFING ESH', 'BRIEFING PNM', 'BRIEFING JET À BRAS'],
    'Encadrement': ['VNM', 'Engins tractés', 'ESH', 'PNM', 'JET À BRAS'],
    'Sécurité': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau', 'Engins tractés'],
    'Pédagogie': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau'],
    'Aisance': ['VNM', 'Jet à bras', 'PNM', 'Engins tractés', 'Bateau'],
    'Procédure d\'intervention': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau'],
    'Animation': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Engins tractés'],
    'Compétences Générales': [
      'Entretien rangement du matériel',
      'Implication générale'
    ],
    'Prérequis': ['Parcours jet à bras', 'Parcours jet à selle', 'Parcours engins tractés'],
    'Épreuves Formatives': [
      'Réglementation',
      'Public',
      'Physio',
      'Anatomie',
      'Météo',
      'Message de secours',
      ...customEpreuves
    ]
  };

  const handleAddCustomItem = async () => {
    if (!customItem.trim()) {
      toast.error('Veuillez entrer un sujet');
      return;
    }

    try {
      await axios.post('/epreuves-formatives', { sujet: customItem.trim() });
      setCustomEpreuves(prev => [...prev, customItem.trim()]);
      setItem(customItem.trim());
      setCustomItem('');
      setShowCustomInput(false);
      toast.success('Sujet ajouté aux épreuves formatives');
    } catch (error) {
      setCustomEpreuves(prev => [...prev, customItem.trim()]);
      setItem(customItem.trim());
      setCustomItem('');
      setShowCustomInput(false);
      toast.success('Sujet ajouté');
    }
  };

  const handleDeleteCustomItem = async (sujet) => {
    try {
      await axios.delete(`/epreuves-formatives/${encodeURIComponent(sujet)}`);
      setCustomEpreuves(prev => prev.filter(e => e !== sujet));
      if (item === sujet) setItem('');
      toast.success('Sujet supprimé');
    } catch (error) {
      setCustomEpreuves(prev => prev.filter(e => e !== sujet));
      if (item === sujet) setItem('');
      toast.success('Sujet supprimé');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedStagiaire || !item) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const stagName = currentStagiaire
        ? `${currentStagiaire.user_info?.prenom || ''} ${currentStagiaire.user_info?.nom || ''}`.trim()
        : '';
      await axios.post('/notes', {
        stagiaire_id: selectedStagiaire,
        formateur_id: user.user_id,
        categorie,
        item,
        note: parseInt(note),
        commentaire,
        commentaire_prive: commentairePrive,
        date: dateNote
      });

      // Confirmation visuelle forte
      setSavedInfo({ stagiaire: stagName, categorie, item, note: parseInt(note) });
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 4000);

      toast.success('Note enregistrée');
      setNote(5);
      setCommentaire('');
      setCommentairePrive('');
      setItem('');
      setDateNote(new Date().toISOString().split('T')[0]);
      loadStagiaireNotes();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("Une note existe déjà pour ce stagiaire, cette catégorie et cet item à cette date");
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0 && score <= 4) return 'text-red-500';
    if (score >= 5 && score <= 7) return 'text-orange-500';
    return 'text-[#8DC63F]';
  };

  const getScoreBackground = (score) => {
    if (score >= 0 && score <= 4) return 'bg-red-50';
    if (score >= 5 && score <= 7) return 'bg-orange-50';
    return 'bg-green-50';
  };

  const currentStagiaire = stagiaires.find(s => s.user_id === selectedStagiaire);

  return (
    <div className="space-y-6" data-testid="notation-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Notation</h1>
        <p className="text-slate-600">Évaluer les stagiaires - Les notes sont visibles dans leur carnet</p>
      </div>

      {/* Bandeau de confirmation visuelle */}
      {justSaved && savedInfo && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-4 duration-300"
          data-testid="note-saved-banner"
        >
          <div className="bg-[#8DC63F] text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 min-w-[320px]">
            <div className="bg-white/20 rounded-full p-2">
              <Check className="h-6 w-6" />
            </div>
            <div>
              <p className="font-bold text-sm">Note enregistrée !</p>
              <p className="text-xs text-white/80 mt-0.5">
                {savedInfo.stagiaire} — {savedInfo.categorie} / {savedInfo.item} : <span className="font-bold text-white">{savedInfo.note}/10</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Stagiaire Banner */}
      {currentStagiaire && (
        <div className="sticky top-0 z-30 bg-[#0F172A] text-white rounded-lg px-5 py-3 shadow-lg flex items-center justify-between" data-testid="sticky-stagiaire-banner">
          <div className="flex items-center gap-3">
            <UserAvatar user={currentStagiaire.user_info || currentStagiaire} size="sm" />
            <div>
              <p className="font-semibold text-sm">
                {currentStagiaire.user_info?.prenom} {currentStagiaire.user_info?.nom}
              </p>
              <p className="text-xs text-slate-400">{currentStagiaire.centre_formation} — {categorie}{item ? ` / ${item}` : ''}</p>
            </div>
          </div>
          <div className={`text-center px-3 py-1 rounded-md ${getScoreBackground(parseInt(note))}`}>
            <span className={`text-lg font-bold ${getScoreColor(parseInt(note))}`}>{note}/10</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sélection Stagiaire - Bandeau prominent */}
        <div className="bg-white border-2 border-[#8DC63F] rounded-lg p-6 shadow-sm" data-testid="stagiaire-selection-card">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-[#8DC63F]" />
            Stagiaire à noter
          </h2>
          <select
            value={selectedStagiaire}
            onChange={(e) => setSelectedStagiaire(e.target.value)}
            className="w-full h-12 px-4 py-2 text-base font-semibold border-2 border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F] focus:border-[#8DC63F] bg-white"
            data-testid="select-stagiaire"
          >
            {stagiaires.map((s) => (
              <option key={s.user_id} value={s.user_id}>
                {s.user_info?.prenom} {s.user_info?.nom} - {s.centre_formation}
              </option>
            ))}
          </select>
          
          {currentStagiaire && (
            <div className="mt-4 p-4 bg-[#8DC63F]/10 border border-[#8DC63F]/30 rounded-lg">
              <p className="text-lg font-bold text-[#0F172A]" data-testid="selected-stagiaire-name">
                {currentStagiaire.user_info?.prenom} {currentStagiaire.user_info?.nom}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                Centre: <span className="font-medium text-[#0F172A]">{currentStagiaire.centre_formation}</span>
                {' • '}
                Statut: <span className="font-medium text-[#0F172A]">{currentStagiaire.statut}</span>
              </p>
            </div>
          )}
        </div>

        {/* Catégorie et Item */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Évaluation</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="categorie">Catégorie</Label>
              <select
                id="categorie"
                value={categorie}
                onChange={(e) => {
                  setCategorie(e.target.value);
                  setItem('');
                  setShowCustomInput(false);
                }}
                className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                data-testid="select-categorie"
              >
                {Object.keys(categories).map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="item">
                Item / Sujet
                {categorie === 'Épreuves Formatives' && (
                  <span className="text-xs text-slate-500 ml-2">(ou ajouter un nouveau)</span>
                )}
              </Label>
              
              {!showCustomInput ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <select
                      id="item"
                      value={item}
                      onChange={(e) => setItem(e.target.value)}
                      className="flex-1 h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                      data-testid="select-item"
                    >
                      <option value="">Sélectionner un item</option>
                      {categories[categorie]?.map((it) => (
                        <option key={it} value={it}>{it}</option>
                      ))}
                    </select>
                    
                    {categorie === 'Épreuves Formatives' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCustomInput(true)}
                        className="px-3"
                        data-testid="btn-add-custom"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  {categorie === 'Épreuves Formatives' && customEpreuves.length > 0 && (
                    <div className="border border-slate-200 rounded-md p-2 space-y-1">
                      <p className="text-xs text-slate-500 font-medium mb-1">Items personnalisés (cliquer pour supprimer) :</p>
                      {customEpreuves.map((ep) => (
                        <div key={ep} className="flex items-center justify-between px-2 py-1 bg-slate-50 rounded text-sm">
                          <span className={item === ep ? 'font-semibold text-[#8DC63F]' : 'text-slate-700'}>{ep}</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteCustomItem(ep)}
                            className="text-red-400 hover:text-red-600 ml-2 p-0.5"
                            data-testid={`delete-custom-${ep}`}
                            title="Supprimer cet item"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={customItem}
                    onChange={(e) => setCustomItem(e.target.value)}
                    placeholder="Nouveau sujet d'épreuve..."
                    className="flex-1"
                    data-testid="input-custom-item"
                  />
                  <Button
                    type="button"
                    onClick={handleAddCustomItem}
                    className="bg-[#8DC63F] hover:bg-[#7db336]"
                    data-testid="btn-confirm-custom"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomItem('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Sélecteur de date */}
            <div className="space-y-2">
              <Label htmlFor="dateNote" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                Date de l'évaluation
              </Label>
              <Input
                type="date"
                id="dateNote"
                value={dateNote}
                onChange={(e) => setDateNote(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full h-10"
                data-testid="input-date-note"
              />
              <p className="text-xs text-slate-500">
                {dateNote === new Date().toISOString().split('T')[0] 
                  ? "Aujourd'hui" 
                  : `${Math.ceil((new Date() - new Date(dateNote)) / (1000 * 60 * 60 * 24))} jour(s) en arrière`}
              </p>
            </div>
          </div>

          {/* Note avec slider */}
          <div className="mt-6 space-y-3">
            <Label>Note: <span className={`text-2xl font-bold ${getScoreColor(parseInt(note))}`}>{note}/10</span></Label>
            
            <div className={`p-4 rounded-lg ${getScoreBackground(parseInt(note))}`}>
              <input
                type="range"
                min="0"
                max="10"
                step="1"
                value={note}
                onChange={(e) => setNote(parseInt(e.target.value))}
                className="w-full h-3 rounded-lg cursor-pointer accent-[#8DC63F]"
                style={{
                  background: `linear-gradient(to right, #8DC63F 0%, #8DC63F ${note * 10}%, #e2e8f0 ${note * 10}%, #e2e8f0 100%)`
                }}
                data-testid="input-note"
              />
              <div className="flex justify-between text-xs text-slate-600 mt-2">
                <span className="text-red-600 font-medium">0-4 Non compétent</span>
                <span className="text-orange-600 font-medium">5-7 En acquisition</span>
                <span className="text-green-600 font-medium">8-10 Acquis</span>
              </div>
            </div>
          </div>

          {/* Commentaire public */}
          <div className="mt-6 space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="commentaire">Commentaire public (visible par le stagiaire et l'entreprise)</Label>
              <VoiceInput
                onTranscript={(text) => setCommentaire(prev => prev ? `${prev} ${text}` : text)}
                className="w-7 h-7"
              />
            </div>
            <textarea
              id="commentaire"
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              placeholder="Observations, conseils, points à améliorer... ou utilisez le micro"
              data-testid="textarea-commentaire"
            />
          </div>

          {/* Commentaire privé */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="commentaire_prive">Note interne (visible uniquement par le centre et les formateurs)</Label>
              <VoiceInput
                onTranscript={(text) => setCommentairePrive(prev => prev ? `${prev} ${text}` : text)}
                className="w-7 h-7"
              />
            </div>
            <textarea
              id="commentaire_prive"
              value={commentairePrive}
              onChange={(e) => setCommentairePrive(e.target.value)}
              rows="2"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 bg-orange-50/30"
              placeholder="Notes internes, points d'attention... ou utilisez le micro"
              data-testid="textarea-commentaire-prive"
            />
            <p className="text-xs text-orange-600">Ce commentaire n'est PAS visible par le stagiaire</p>
          </div>

          {/* Mémo dernière note */}
          {item && (
            <div className="mt-4" data-testid="last-note-memo">
              {lastNote ? (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <History className="h-4 w-4 text-slate-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Dernière note — {categorie} / {item}</span>
                  </div>
                  <div className="flex items-center gap-4 mb-2">
                    <span className={`text-xl font-bold ${getScoreColor(normalizeNote(lastNote))}`}>{normalizeNote(lastNote).toFixed(1)}/10</span>
                    <span className="text-xs text-slate-400">
                      {lastNote.date ? new Date(lastNote.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    </span>
                  </div>
                  {lastNote.commentaire && (
                    <div className="flex items-start gap-2 text-sm text-slate-700 bg-white rounded p-2 border border-slate-100">
                      <MessageSquare className="h-3.5 w-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                      <span>{lastNote.commentaire}</span>
                    </div>
                  )}
                  {lastNote.commentaire_prive && (
                    <div className="flex items-start gap-2 text-sm text-orange-700 bg-orange-50 rounded p-2 border border-orange-100 mt-2">
                      <MessageSquare className="h-3.5 w-3.5 text-orange-400 mt-0.5 flex-shrink-0" />
                      <span className="italic">{lastNote.commentaire_prive}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3 flex items-center gap-2">
                  <History className="h-4 w-4 text-slate-300" />
                  <span className="text-sm text-slate-400">Aucune note précédente pour {categorie} / {item}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            className={`px-8 font-semibold shadow-md transition-all duration-300 ${
              justSaved
                ? 'bg-[#8DC63F] hover:bg-[#8DC63F] text-white scale-105'
                : 'bg-[#0F172A] hover:bg-[#1e293b] text-white'
            }`}
            data-testid="submit-note-button"
          >
            {justSaved ? (
              <>
                <Check className="h-5 w-5 mr-2" />
                Enregistrée !
              </>
            ) : (
              'Enregistrer la Note'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Notation;
